import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Toaster } from './ui/sonner';

export function Layout({ children }) {
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white" lang={language === 'es' ? 'es' : 'en'}>
      {/* Skip to main content link for keyboard/screen reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[#00599c] focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:font-medium"
      >
        {language === 'es' ? 'Saltar al contenido principal' : 'Skip to main content'}
      </a>

      {/* Header - only on internal pages with just "Volver" button */}
      {!isHomePage && (
        <header 
          className="sticky top-0 z-50 bg-[#00599c] text-white"
          role="banner"
        >
          <nav 
            className="max-w-7xl mx-auto px-4"
            aria-label={language === 'es' ? 'Navegación principal' : 'Main navigation'}
          >
            <div className="flex items-center justify-between h-14">
              {/* Back button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="flex items-center gap-2 text-white hover:bg-white/10 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#00599c] min-h-[44px] min-w-[44px]"
                data-testid="back-button"
                aria-label={language === 'es' ? 'Volver a la página principal' : 'Back to home page'}
              >
                <ArrowLeft size={20} aria-hidden="true" />
                <span className="font-medium">{language === 'es' ? 'Volver' : 'Back'}</span>
              </Button>

              {/* Logo centered */}
              <Link 
                to="/" 
                className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#00599c] rounded-lg p-1" 
                data-testid="logo-link"
                aria-label={language === 'es' ? 'Tenerife Cerca - Ir al inicio' : 'Tenerife Cerca - Go to home'}
              >
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <span className="text-[#00599c] font-bold text-base font-['Outfit']">T</span>
                </div>
                <span className="text-white font-semibold font-['Outfit'] text-base whitespace-nowrap">
                  Tenerife Cerca
                </span>
              </Link>

              {/* Language Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                data-testid="language-toggle"
                aria-label={language === 'es' ? 'Cambiar idioma a inglés' : 'Switch language to Spanish'}
                className="flex items-center gap-2 text-white hover:bg-white/10 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#00599c] min-h-[44px] min-w-[44px]"
              >
                <Globe size={16} aria-hidden="true" />
                <span className="font-medium uppercase text-sm">{language}</span>
              </Button>
            </div>
          </nav>
        </header>
      )}

      {/* Main Content */}
      <main id="main-content" className="flex-1" role="main" tabIndex="-1">
        {children}
      </main>

      {/* Footer - only on non-home pages */}
      {!isHomePage && (
        <footer className="mt-auto py-6 bg-white" role="contentinfo">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <Link 
              to="/about" 
              className="text-sm text-[#525966] hover:text-[#00599c] transition-colors focus:outline-none focus:ring-2 focus:ring-[#00599c] focus:ring-offset-2 rounded px-2 py-1"
              aria-label={language === 'es' ? 'Ir a la página Acerca de este proyecto' : 'Go to About this project page'}
            >
              {language === 'es' ? 'Acerca de este proyecto' : 'About this project'} →
            </Link>
          </div>
        </footer>
      )}

      {/* Toast notifications - accessible by default */}
      <Toaster position="top-right" />
    </div>
  );
}
